/**
 * HeroSection — "Sealed Verdict" aesthetic
 * ----------------------------------------------------------------------------
 * Metaphor map (replaces the original Cyber-Medical brief):
 *   The Gavel Strike  → gold ruling line sweeps top→bottom on load     ~0.6s
 *   The Seal Engraving→ SVG "sign-here" path draws itself once          ~2.2s (App.css)
 *   Alive Monogram    → lives in the Navbar (breathing brand mark)      3.5s loop
 *   Scanline          → 1px gold line drifts down for atmosphere        7s loop (App.css)
 *   Live Keyword Glow → the word "Rights" breathes a gold gradient      4s loop (App.css)
 *   The Verdict Button→ rotating gold conic border on hover             4s/rev (App.css)
 *
 * All motion is disabled under prefers-reduced-motion (Framer useReducedMotion
 * + App.css media query) — everything degrades to a static, fully-visible hero.
 */
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Scale, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

// Cubic-bezier easing, typed as a 4-tuple so it satisfies Framer's Easing type.
const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

// How many times the sector list is repeated across the marquee track. Must be
// EVEN so each half (translateX -50%) ends exactly on a sector boundary, and
// large enough that one half overflows even very wide screens (6 × 8 sectors
// ≈ 3 viewport-widths per half).
const MARQUEE_REPEAT = 6;

export function HeroSection() {
  const { t } = useLang();
  const reduce = useReducedMotion();

  const fadeUp = (delay: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, ease: EASE_OUT, delay },
        };

  return (
    <section id="banner-0" className="relative overflow-hidden">
      {/* The Gavel Strike — vertical gold ruling line sweeps down on load */}
      {!reduce && (
        <motion.span
          aria-hidden
          className="absolute left-6 top-0 z-20 w-px bg-gradient-to-b from-secondary via-secondary/60 to-transparent md:left-24 rtl:left-auto rtl:right-6 md:rtl:right-24"
          initial={{ height: 0, opacity: 0.9 }}
          animate={{ height: "100%", opacity: 0.5 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        />
      )}

      {/* Scanline atmosphere (hidden on small screens to save paint) */}
      {!reduce && <span className="scanline hidden sm:block" aria-hidden />}

      <div className="container relative z-10 mx-auto flex min-h-[calc(100vh_-_72px)] flex-col justify-center px-6 pb-16 pt-28 lg:px-24 lg:pt-36">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.span
              {...fadeUp(0.5)}
              className="inline-flex items-center gap-3 text-xs uppercase tracking-eyebrow text-gold-deep"
            >
              <span className="h-px w-8 bg-secondary" aria-hidden />
              {t.hero.eyebrow}
            </motion.span>

            <motion.h1
              {...fadeUp(0.65)}
              className="mt-6 font-serif text-5xl font-semibold leading-[0.95] text-primary md:text-8xl"
            >
              {t.hero.titlePre}
              <span className="keyword-glow">{t.hero.titleHighlight}</span>
              {t.hero.titlePost}
            </motion.h1>

            <motion.p
              {...fadeUp(0.8)}
              className="mt-4 font-serif text-2xl italic text-primary/70 md:text-3xl rtl:not-italic"
            >
              {t.hero.tagline}
            </motion.p>

            <motion.p
              {...fadeUp(1.05)}
              className="mt-7 max-w-xl text-lg leading-relaxed text-primary/75"
            >
              {t.hero.paragraph}
            </motion.p>

            <motion.div {...fadeUp(1.2)} className="mt-9">
              <a
                href="#contact"
                className="verdict-btn group inline-flex items-center gap-3 rounded-full bg-primary px-8 py-4 text-base text-ivory ring-1 ring-secondary/50 transition-shadow hover:shadow-[0_0_30px_rgba(212,161,90,0.35)]"
              >
                <span className="font-medium tracking-wide">{t.hero.cta}</span>
                <ArrowRight className="h-5 w-5 text-secondary transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </a>
            </motion.div>
          </div>

          {/* Abstract counsel dashboard — desktop only */}
          <motion.div
            {...fadeUp(0.95)}
            className="hidden lg:col-span-5 lg:block"
            aria-hidden
          >
            <CounselDashboard float={!reduce} />
          </motion.div>
        </div>
      </div>

      {/* Sector marquee — the sectors are repeated enough times that each
          half of the track always overflows the viewport, so the -50% loop
          (App.css) never reveals a gap. Spacing is baked into each item
          (mx on the separator) rather than a container gap, so the two halves
          tile perfectly and the seam is invisible. MARQUEE_REPEAT must stay
          even so the half lands on a sector boundary. */}
      <div className="relative z-10 border-y border-primary/15 bg-primary/[0.04] py-4">
        <div className="marquee-track items-center text-sm uppercase tracking-[0.25em] text-primary/60">
          {Array.from({ length: MARQUEE_REPEAT }, () => t.hero.sectors)
            .flat()
            .map((s, i) => (
              <span key={i} className="flex items-center whitespace-nowrap">
                {s}
                <span className="mx-8 text-secondary" aria-hidden>
                  ·
                </span>
              </span>
            ))}
        </div>
      </div>
    </section>
  );
}

/** Code-only "sealed case file" composition — CSS transforms, no images. */
function CounselDashboard({ float }: { float: boolean }) {
  const { t } = useLang();
  return (
    <motion.div
      className="relative mx-auto w-full max-w-sm"
      animate={float ? { y: [0, -10, 0] } : undefined}
      transition={
        float ? { duration: 6, repeat: Infinity, ease: "easeInOut" } : undefined
      }
      style={{ transformStyle: "preserve-3d" }}
    >
      <div
        className="rounded-2xl border border-primary/15 bg-white/85 p-6 shadow-[0_30px_60px_rgba(5,37,45,0.14)] backdrop-blur-sm"
        style={{ transform: "perspective(1200px) rotateY(-8deg) rotateX(4deg)" }}
      >
        <div className="flex items-center justify-between border-b border-primary/10 pb-4">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-secondary" />
            <span className="font-serif text-sm tracking-wide text-primary">
              {t.hero.dash.caseFile}
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-eyebrow text-gold-deep/80">
            {t.hero.dash.confidential}
          </span>
        </div>

        {/* Fake document rows */}
        <div className="mt-5 space-y-3">
          {[
            "w-full",
            "w-5/6",
            "w-2/3",
            "w-3/4",
          ].map((w, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
              <span className={`h-2 ${w} rounded-full bg-primary/10`} />
            </div>
          ))}
        </div>

        {/* Verdict meter */}
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-eyebrow text-primary/60">
            <span>{t.hero.dash.verdict}</span>
            <span className="text-gold-deep">{t.hero.dash.percent}</span>
          </div>
          <div className="relative h-1.5 rounded-full bg-primary/10">
            <div className="absolute inset-y-0 left-0 w-full rounded-full bg-gradient-to-r from-secondary/40 to-secondary" />
            <span className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-secondary shadow-[0_0_10px_rgba(212,161,90,0.7)]" />
          </div>
        </div>
      </div>

      {/* Floating wax-seal stamp */}
      <div className="absolute -bottom-5 -left-5 grid h-20 w-20 -rotate-12 place-items-center rounded-full border-2 border-dashed border-secondary/70 bg-white/90 shadow-[0_0_25px_rgba(212,161,90,0.3)] rtl:left-auto rtl:-right-5">
        <ShieldCheck className="h-8 w-8 text-secondary" />
      </div>
    </motion.div>
  );
}
